function readItemCategory(item) {
  return String(item?.category || item?.category_name || "").trim();
}

function readItemSubcategory(item) {
  return String(item?.subcategory || item?.sub_category || item?.subCategory || "").trim();
}

function toSelectedCategorySet(selectedCategories) {
  if (selectedCategories instanceof Set) return selectedCategories;
  if (Array.isArray(selectedCategories)) {
    return new Set(selectedCategories.map((x) => String(x || "").trim()).filter(Boolean));
  }
  return new Set();
}

/** All category names from the master catalog tree, with optional fallback from loaded items. */
export function getCategoryOptionsFromCatalog(allCatalog, fallbackItems = []) {
  const cats = Array.isArray(allCatalog?.categories) ? allCatalog.categories : [];
  const fromCatalog = cats
    .map((c) => String(c?.category || "").trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  if (fromCatalog.length > 0) return fromCatalog;

  const fromItems = new Set();
  (Array.isArray(fallbackItems) ? fallbackItems : []).forEach((item) => {
    const v = readItemCategory(item);
    if (v) fromItems.add(v);
  });
  return Array.from(fromItems).sort((a, b) => a.localeCompare(b));
}

/**
 * Subcategory names from the master catalog tree.
 * When categories are selected, only subcategories for those categories are returned.
 * With no category selected, all subcategories are returned.
 */
export function getSubcategoryOptionsFromCatalog(
  allCatalog,
  fallbackItems = [],
  selectedCategories = null
) {
  const selected = toSelectedCategorySet(selectedCategories);
  const cats = Array.isArray(allCatalog?.categories) ? allCatalog.categories : [];
  const subs = new Set();

  if (cats.length > 0) {
    cats.forEach((c) => {
      const categoryName = String(c?.category || "").trim();
      if (!categoryName) return;
      if (selected.size > 0 && !selected.has(categoryName)) return;
      (Array.isArray(c?.subcategories) ? c.subcategories : []).forEach((sc) => {
        const v = String(sc || "").trim();
        if (v) subs.add(v);
      });
    });
    if (subs.size > 0 || selected.size > 0) {
      return Array.from(subs).sort((a, b) => a.localeCompare(b));
    }
  }

  (Array.isArray(fallbackItems) ? fallbackItems : []).forEach((item) => {
    const itemCategory = readItemCategory(item);
    if (selected.size > 0 && itemCategory && !selected.has(itemCategory)) return;
    const sub = readItemSubcategory(item);
    if (sub) subs.add(sub);
  });
  return Array.from(subs).sort((a, b) => a.localeCompare(b));
}

/** Toggle one category; prune subcategories that no longer apply when a category is removed. */
export function toggleCatalogCategorySelection({
  selectedCategories,
  selectedSubcategories,
  category,
  checked,
  catalog = null,
}) {
  const categories = new Set(selectedCategories);
  if (checked) categories.add(category);
  else categories.delete(category);

  let subcategories =
    selectedSubcategories instanceof Set
      ? new Set(selectedSubcategories)
      : new Set(Array.isArray(selectedSubcategories) ? selectedSubcategories : []);

  if (!checked && catalog) {
    const validSubcategories = new Set(
      getSubcategoryOptionsFromCatalog(catalog, [], categories)
    );
    subcategories = new Set([...subcategories].filter((value) => validSubcategories.has(value)));
  }

  return { categories, subcategories };
}
